import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiService } from '../../../services/api';
import './QuickRuleBuilder.css';

/**
 * QuickRuleBuilder - Streamlined gate creation with chip-based node search.
 *
 * Designed for rapidly wiring condition nodes through AND/OR gates to
 * combination nodes. System handles all underlying JSON, synapses, and
 * Platform settings automatically.
 *
 * Features:
 * - Chip-based multi-select (like email "To:" field) for input & output nodes
 * - AND/OR gate type toggle
 * - Auto-generated gate name from selected node names
 * - Live connection preview
 * - "Create & Add Another" for rapid chaining
 * - Recently-used nodes for quick re-selection
 */

/* ------------------------------------------------------------------ */
/*  ChipNodeSearch - typeahead multi-select that renders picked nodes  */
/*  as removable chips                                                 */
/* ------------------------------------------------------------------ */
function ChipNodeSearch({ label, required, graphId, selectedNodes, onAdd, onRemove, recentNodes, placeholder }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const search = useCallback(async (q) => {
    if (!q || q.length < 2 || !graphId) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const resp = await apiService.listNodes({ graphId, query: q, pageSize: 20 });
      if (resp.success) {
        setResults(resp.nodes || []);
      }
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [graphId]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(query), 250);
    return () => clearTimeout(debounceRef.current);
  }, [query, search]);

  const selectedIds = new Set(selectedNodes.map(n => n.id));

  // Build dropdown items: recent nodes first (if no query), then search results
  const dropdownItems = [];
  if (!query && recentNodes && recentNodes.length > 0) {
    const unselectedRecent = recentNodes.filter(n => !selectedIds.has(n.id));
    if (unselectedRecent.length > 0) {
      dropdownItems.push({ type: 'header', label: 'Recently Used' });
      unselectedRecent.forEach(n => dropdownItems.push({ type: 'node', node: n }));
    }
  }
  if (query.length >= 2) {
    const filtered = results.filter(n => !selectedIds.has(n.id || n.nodeId));
    filtered.forEach(n => {
      // Find if match was via an alias (for display hint)
      const nodeAliases = n.aliases || [];
      const q = query.toLowerCase();
      const matchingAlias = nodeAliases.find(a =>
        a.toLowerCase().includes(q) && a.toLowerCase() !== (n.name || n.nodeName || '').toLowerCase()
      );
      dropdownItems.push({
        type: 'node',
        node: {
          id: n.id || n.nodeId,
          name: n.name || n.nodeName,
          type: n.type || n.nodeType || '',
          matchingAlias: matchingAlias || null,
        },
      });
    });
  }

  const handleSelect = (node) => {
    onAdd(node);
    setQuery('');
    setResults([]);
  };

  return (
    <div className="qrb-field" ref={containerRef}>
      <label>
        {label} {required && <span className="qrb-required">*</span>}
      </label>
      <div className="qrb-chip-input">
        {selectedNodes.map((node) => (
          <span key={node.id} className="qrb-chip">
            <span className="qrb-chip-name">{node.name}</span>
            <button
              type="button"
              className="qrb-chip-remove"
              onClick={() => onRemove(node.id)}
              aria-label={`Remove ${node.name}`}
            >
              &times;
            </button>
          </span>
        ))}
        <input
          type="text"
          className="qrb-chip-text-input"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setShowDropdown(true); }}
          onFocus={() => setShowDropdown(true)}
          placeholder={selectedNodes.length === 0 ? (placeholder || 'Type to search nodes...') : 'Add more...'}
        />
      </div>
      {showDropdown && (dropdownItems.length > 0 || (query.length >= 2 && !loading)) && (
        <div className="qrb-dropdown">
          {loading && <div className="qrb-dropdown-msg">Searching...</div>}
          {!loading && query.length >= 2 && dropdownItems.length === 0 && (
            <div className="qrb-dropdown-msg">No nodes found</div>
          )}
          {dropdownItems.map((item, idx) => {
            if (item.type === 'header') {
              return <div key={`hdr-${idx}`} className="qrb-dropdown-header">{item.label}</div>;
            }
            return (
              <div
                key={item.node.id}
                className="qrb-dropdown-item"
                onClick={() => handleSelect(item.node)}
              >
                <span className="qrb-dropdown-item-name">
                  {item.node.name}
                  {item.node.matchingAlias && (
                    <span style={{ color: '#888', fontSize: '0.85em', marginLeft: 4 }}>
                      (aka: {item.node.matchingAlias})
                    </span>
                  )}
                </span>
                {item.node.type && (
                  <span className="qrb-dropdown-item-type">{item.node.type}</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  QuickRuleBuilder main component                                    */
/* ------------------------------------------------------------------ */
function QuickRuleBuilder({ isOpen, onClose, onGateCreated, graphId }) {
  const [inputNodes, setInputNodes] = useState([]);
  const [outputNodes, setOutputNodes] = useState([]);
  const [gateType, setGateType] = useState('AND');
  const [nameOverride, setNameOverride] = useState('');
  const [nameEdited, setNameEdited] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [recentNodes, setRecentNodes] = useState([]);
  const [createdCount, setCreatedCount] = useState(0);

  // Auto-generate name from selected nodes
  const autoName = (() => {
    if (inputNodes.length === 0 && outputNodes.length === 0) return '';
    const inputPart = inputNodes.map(n => n.name).join(` ${gateType} `);
    if (outputNodes.length > 0) {
      return `${inputPart} -> ${outputNodes.map(n => n.name).join(', ')}`;
    }
    return inputPart;
  })();

  const effectiveName = nameEdited && nameOverride ? nameOverride : autoName;

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      resetForm();
      setCreatedCount(0);
    }
  }, [isOpen]);

  const resetForm = () => {
    setInputNodes([]);
    setOutputNodes([]);
    setGateType('AND');
    setNameOverride('');
    setNameEdited(false);
    setError(null);
    setSuccess(null);
  };

  // Track recently used nodes (in-memory, persists across creates within session)
  const trackRecent = (nodes) => {
    setRecentNodes(prev => {
      const ids = new Set(prev.map(n => n.id));
      const newNodes = nodes.filter(n => !ids.has(n.id));
      return [...newNodes, ...prev].slice(0, 20);
    });
  };

  const addInputNode = (node) => {
    if (!inputNodes.find(n => n.id === node.id)) {
      setInputNodes(prev => [...prev, node]);
    }
  };

  const removeInputNode = (nodeId) => {
    setInputNodes(prev => prev.filter(n => n.id !== nodeId));
  };

  const addOutputNode = (node) => {
    if (!outputNodes.find(n => n.id === node.id)) {
      setOutputNodes(prev => [...prev, node]);
    }
  };

  const removeOutputNode = (nodeId) => {
    setOutputNodes(prev => prev.filter(n => n.id !== nodeId));
  };

  const validate = () => {
    if (inputNodes.length < 2) {
      setError('Select at least 2 input nodes for the gate condition');
      return false;
    }
    if (outputNodes.length === 0) {
      setError('Select at least 1 output node');
      return false;
    }
    return true;
  };

  const handleCreate = async (keepOpen) => {
    if (!validate()) return;

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const payload = {
        graphId,
        gateType,
        name: effectiveName || `${gateType} Gate`,
        description: `${gateType} gate: ${inputNodes.map(n => n.name).join(' + ')} -> ${outputNodes.map(n => n.name).join(', ')}`,
        inputs: inputNodes.map(n => n.id),
        outputs: outputNodes.map(n => n.id),
      };

      const response = await apiService.createGate(payload);

      if (response.success) {
        trackRecent([...inputNodes, ...outputNodes]);
        setCreatedCount(prev => prev + 1);
        setSuccess(`Gate created successfully!`);

        if (onGateCreated) {
          onGateCreated(response.gate || response);
        }

        if (keepOpen) {
          // Reset inputs but keep modal open for next rule
          setTimeout(() => {
            setInputNodes([]);
            setOutputNodes([]);
            setNameOverride('');
            setNameEdited(false);
            setSuccess(null);
          }, 800);
        } else {
          setTimeout(() => onClose(), 600);
        }
      } else {
        throw new Error(response.message || 'Failed to create gate');
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to create gate');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="qrb-overlay" onClick={onClose}>
      <div className="qrb-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="qrb-header">
          <div>
            <p className="qrb-eyebrow">Quick Rule Builder</p>
            <h2>Connect Nodes with Gate</h2>
          </div>
          <div className="qrb-header-right">
            {createdCount > 0 && (
              <span className="qrb-created-badge">{createdCount} created</span>
            )}
            <button className="qrb-close" onClick={onClose}>&times;</button>
          </div>
        </div>

        {/* Body */}
        <div className="qrb-body">
          {error && <div className="qrb-alert qrb-alert--error">{error}</div>}
          {success && <div className="qrb-alert qrb-alert--success">{success}</div>}

          {/* Step 1: Input Nodes */}
          <div className="qrb-section">
            <div className="qrb-section-label">When these conditions are met...</div>
            <ChipNodeSearch
              label="Input Nodes"
              required
              graphId={graphId}
              selectedNodes={inputNodes}
              onAdd={addInputNode}
              onRemove={removeInputNode}
              recentNodes={recentNodes}
              placeholder="Search condition nodes..."
            />
          </div>

          {/* Step 2: Gate Type */}
          <div className="qrb-section">
            <div className="qrb-section-label">Gate Logic</div>
            <div className="qrb-gate-toggle">
              <button
                type="button"
                className={`qrb-gate-btn ${gateType === 'AND' ? 'active' : ''}`}
                onClick={() => setGateType('AND')}
              >
                <span className="qrb-gate-btn-label">AND</span>
                <span className="qrb-gate-btn-desc">All must fire</span>
              </button>
              <button
                type="button"
                className={`qrb-gate-btn ${gateType === 'OR' ? 'active' : ''}`}
                onClick={() => setGateType('OR')}
              >
                <span className="qrb-gate-btn-label">OR</span>
                <span className="qrb-gate-btn-desc">Any one fires</span>
              </button>
            </div>
          </div>

          {/* Step 3: Output Nodes */}
          <div className="qrb-section">
            <div className="qrb-section-label">Then activate...</div>
            <ChipNodeSearch
              label="Output Nodes"
              required
              graphId={graphId}
              selectedNodes={outputNodes}
              onAdd={addOutputNode}
              onRemove={removeOutputNode}
              recentNodes={recentNodes}
              placeholder="Search result/combination nodes..."
            />
          </div>

          {/* Auto Name */}
          <div className="qrb-section">
            <div className="qrb-section-label">Gate Name</div>
            <div className="qrb-field">
              <input
                type="text"
                className="qrb-name-input"
                value={nameEdited ? nameOverride : autoName}
                onChange={(e) => { setNameOverride(e.target.value); setNameEdited(true); }}
                placeholder="Auto-generated from selections..."
              />
              {nameEdited && (
                <button
                  type="button"
                  className="qrb-name-reset"
                  onClick={() => { setNameEdited(false); setNameOverride(''); }}
                >
                  Reset to auto
                </button>
              )}
            </div>
          </div>

          {/* Connection Preview */}
          {(inputNodes.length > 0 || outputNodes.length > 0) && (
            <div className="qrb-preview">
              <div className="qrb-preview-label">Connection Preview</div>
              <div className="qrb-preview-flow">
                <div className="qrb-preview-group">
                  {inputNodes.map(n => (
                    <span key={n.id} className="qrb-preview-pill qrb-preview-pill--input">{n.name}</span>
                  ))}
                  {inputNodes.length === 0 && (
                    <span className="qrb-preview-empty">No inputs</span>
                  )}
                </div>
                <div className="qrb-preview-arrow-section">
                  <span className="qrb-preview-arrow">&rarr;</span>
                  <span className="qrb-preview-gate-badge">{gateType}</span>
                  <span className="qrb-preview-arrow">&rarr;</span>
                </div>
                <div className="qrb-preview-group">
                  {outputNodes.map(n => (
                    <span key={n.id} className="qrb-preview-pill qrb-preview-pill--output">{n.name}</span>
                  ))}
                  {outputNodes.length === 0 && (
                    <span className="qrb-preview-empty">No outputs</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="qrb-footer">
          <button
            type="button"
            className="qrb-btn qrb-btn--secondary"
            onClick={onClose}
            disabled={saving}
          >
            {createdCount > 0 ? 'Done' : 'Cancel'}
          </button>
          <button
            type="button"
            className="qrb-btn qrb-btn--outline"
            onClick={() => handleCreate(false)}
            disabled={saving || inputNodes.length < 2 || outputNodes.length === 0}
          >
            {saving ? 'Creating...' : 'Create & Close'}
          </button>
          <button
            type="button"
            className="qrb-btn qrb-btn--primary"
            onClick={() => handleCreate(true)}
            disabled={saving || inputNodes.length < 2 || outputNodes.length === 0}
          >
            {saving ? 'Creating...' : 'Create & Add Another'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default QuickRuleBuilder;
